import { Link, useRouterState } from "@tanstack/react-router";
import { FileText, Home, Map, PanelLeft, Sparkles } from "lucide-react";
import { useMemo, useState, type ComponentType } from "react";

import { useCurrentRole } from "../../api/auth.js";
import { useFeatureFlags } from "../../api/feature-flags.js";
import { splitStaffRail } from "../../lib/staff-rail-model.js";

import { filterNavSectionsForFlags, filterNavSectionsForRole } from "./nav-config.js";
import { isActiveHref } from "./resolve-sidebar-state.js";

const RAIL_KEY = "l30:staff:rail";

const PRIMARY_LABELS: Record<string, string> = {
  "insights-home": "Home",
  "ideation-maps": "Maps",
  documents: "Working documents",
  "ask-insights": "Ask Insights",
};

const PRIMARY_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  "insights-home": Home,
  "ideation-maps": Map,
  documents: FileText,
  "ask-insights": Sparkles,
};

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(RAIL_KEY) === "collapsed";
  } catch {
    return false;
  }
}

/**
 * Desktop staff rail from the approved shell: Home, Maps, Working documents,
 * Ask Insights, then the rest of the existing module inventory.
 */
export function StaffRail() {
  const { location } = useRouterState();
  const currentPath = location.pathname;
  const role = useCurrentRole();
  const flagsQuery = useFeatureFlags();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const enabledFlags = useMemo(() => {
    const set = new Set<string>();
    for (const flag of flagsQuery.data?.flags ?? []) {
      if (flag.enabled) set.add(flag.key);
    }
    return set;
  }, [flagsQuery.data]);
  const { primary, more } = useMemo(() => {
    const visible = filterNavSectionsForFlags(filterNavSectionsForRole(role), enabledFlags);
    return splitStaffRail(visible);
  }, [role, enabledFlags]);

  function toggleCollapsed() {
    setCollapsed((current) => {
      const next = !current;
      try {
        localStorage.setItem(RAIL_KEY, next ? "collapsed" : "expanded");
      } catch {
        // Preference is session-only when storage is blocked.
      }
      return next;
    });
  }

  return (
    <nav
      aria-label="Staff"
      className={`hidden h-screen shrink-0 flex-col border-r border-border bg-surface md:flex ${
        collapsed ? "w-14" : "w-56"
      }`}
    >
      <div
        className={`flex items-center ${collapsed ? "justify-center px-2" : "justify-between px-3"} py-3`}
      >
        {collapsed ? null : (
          <p className="text-xs font-semibold uppercase tracking-widest text-fg-subtle">Insights</p>
        )}
        <button
          type="button"
          onClick={toggleCollapsed}
          className="rounded-md p-2 text-fg-subtle hover:bg-surface-muted hover:text-fg"
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          title={collapsed ? "Expand navigation" : "Collapse navigation"}
        >
          <PanelLeft className="size-4" aria-hidden="true" />
        </button>
      </div>
      <ul className="flex flex-col gap-1 px-2">
        {primary.map((item) => {
          const Icon = PRIMARY_ICONS[item.id] ?? Home;
          const label = PRIMARY_LABELS[item.id] ?? item.label;
          const active = isActiveHref(item.href, currentPath);
          return (
            <li key={item.id}>
              <Link
                to={item.href}
                aria-current={active ? "page" : undefined}
                title={label}
                className={`flex items-center gap-3 rounded-md px-2 py-2 text-sm ${
                  active
                    ? "bg-surface-muted font-semibold text-fg"
                    : "text-fg-subtle hover:bg-surface-muted hover:text-fg"
                } ${collapsed ? "justify-center" : ""}`}
              >
                <Icon className="size-4 shrink-0" aria-hidden="true" />
                {collapsed ? <span className="sr-only">{label}</span> : label}
              </Link>
            </li>
          );
        })}
      </ul>
      {collapsed || more.length === 0 ? null : (
        <details className="mt-4 min-h-0 flex-1 overflow-auto px-2 pb-4" open>
          <summary className="cursor-pointer px-2 py-2 text-xs font-semibold uppercase tracking-widest text-fg-subtle">
            All modules
          </summary>
          {more.map((section) => (
            <div key={section.id} className="mt-3">
              <p className="px-2 text-[10px] font-semibold uppercase tracking-wider text-fg-muted">
                {section.label}
              </p>
              <ul className="mt-1 flex flex-col gap-0.5">
                {section.items.map((item) => {
                  const active = isActiveHref(item.href, currentPath);
                  return (
                    <li key={item.id}>
                      <Link
                        to={item.href}
                        aria-current={active ? "page" : undefined}
                        className={`block rounded-md px-2 py-1.5 text-sm ${
                          active
                            ? "bg-surface-muted font-semibold text-fg"
                            : "text-fg-subtle hover:bg-surface-muted hover:text-fg"
                        }`}
                      >
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </details>
      )}
    </nav>
  );
}
